import { Fragment, useEffect, useState } from "react";
import { Car, ChevronLeft, ChevronRight, Download, FileCheck2, Loader2, MessageCircle, PhoneCall, Search, ShieldCheck, Wrench } from "lucide-react";
import api, { API, fmtIDR, fmtDate } from "@/lib/api";

function DownloadPhotosButton({ car, compact }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/public/cars/${car.id}/photos.jpg`);
      if (!res.ok) throw new Error("Foto tidak tersedia");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(car.name || "mobil").replace(/\s+/g, "_")}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setErr(e.message || "Gagal mengunduh");
      setTimeout(() => setErr(""), 4000);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid={`download-photos-${car.id}`}
      onClick={run}
      disabled={busy}
      aria-busy={busy}
      title={busy ? "Menyiapkan foto, mohon tunggu..." : "Unduh semua foto unit ini (1 gambar)"}
      className={`inline-flex items-center justify-center h-10 rounded-md border text-sm font-semibold transition-colors duration-200 disabled:cursor-wait ${err ? "border-rose-600/50 text-rose-400" : busy ? "border-blue-600/50 text-blue-400 bg-blue-600/10" : "border-[#26262E] text-zinc-300 hover:bg-[#121218] hover:text-zinc-100"} ${compact && !busy && !err ? "px-3.5" : "flex-1 px-3"}`}
    >
      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className={`w-4 h-4 ${compact && !err ? "" : "mr-1.5"}`} />}
      {busy ? <span data-testid={`download-photos-loading-${car.id}`}>Menyiapkan foto…</span> : err ? err : compact ? "" : "Unduh Foto"}
    </button>
  );
}

const fmtNum = (n) => new Intl.NumberFormat("id-ID").format(Number(n) || 0);
const fmtKm = (km) => `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format((Number(km) || 0) / 1000)}k`;
const normMerk = (m) => (m || "-").trim().replace(/\s+/g, " ");

function waLink(phone, car) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const intl = digits.startsWith("0") ? "62" + digits.slice(1) : digits;
  const price = car.cash_price ? ` harga cash ${fmtIDR(car.cash_price)}` : "";
  const msg = encodeURIComponent(`Halo ${car.branch_name !== "-" ? car.branch_name : "ForzaMobil.id"}, saya tertarik dengan ${car.name} (${car.year})${price}. Apakah masih tersedia?`);
  return `https://wa.me/${intl}?text=${msg}`;
}

function sortedPackages(car) {
  return [...(car.credit_packages || [])].sort((a, b) => (a.tdp || 0) - (b.tdp || 0));
}

function packageLabel(car, idx) {
  const n = (car.credit_packages || []).length;
  if (n <= 1) return "Paket Kredit";
  if (idx === 0) return "Paket Kredit TDP Ringan";
  if (idx === n - 1) return "Paket Kredit Bunga Ringan";
  return "Paket Kredit";
}

function CardGallery({ car }) {
  const [idx, setIdx] = useState(0);
  const n = car.photo_count || (car.has_photo ? 1 : 0);
  if (!n) {
    return (
      <div className="aspect-video bg-[#121218] flex items-center justify-center relative">
        <Car className="w-10 h-10 text-zinc-700" />
        {car.status === "booking" && (
          <span data-testid={`booking-badge-${car.id}`} className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border text-amber-400 bg-amber-500/15 border-amber-500/40">Booking</span>
        )}
      </div>
    );
  }
  const cur = Math.min(idx, n - 1);
  return (
    <div className="aspect-video bg-[#121218] relative group">
      <img src={`${API}/public/photo/${car.id}/${cur}?w=900`} alt={car.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      {car.status === "booking" && (
        <span data-testid={`booking-badge-${car.id}`} className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border text-amber-400 bg-amber-500/15 border-amber-500/40 backdrop-blur-sm">Booking</span>
      )}
      {n > 1 && (
        <>
          <button data-testid={`gallery-prev-${car.id}`} onClick={() => setIdx((cur - 1 + n) % n)} className="print-hide absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#050505]/70 text-zinc-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button data-testid={`gallery-next-${car.id}`} onClick={() => setIdx((cur + 1) % n)} className="print-hide absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#050505]/70 text-zinc-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="print-hide absolute bottom-2 right-2 text-[10px] font-mono bg-[#050505]/70 text-zinc-300 px-2 py-0.5 rounded-sm">{cur + 1}/{n}</span>
        </>
      )}
    </div>
  );
}

export default function PublicPricelist() {
  const [cars, setCars] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get(`/public/pricelist?t=${Date.now()}`).then((r) => setCars(r.data)).catch(() => setError("Gagal memuat pricelist"));
  }, []);

  const branchOptions = [...new Set((cars || []).map((c) => c.branch_name).filter((b) => b && b !== "-"))];
  const filtered = (cars || []).filter((c) =>
    (!q.trim() || `${c.name} ${c.merk || ""}`.toLowerCase().includes(q.trim().toLowerCase())) &&
    (branchFilter === "all" || c.branch_name === branchFilter) &&
    (statusFilter === "all" || c.status === statusFilter)
  ).sort((a, b) => (a.status === "booking" ? 1 : 0) - (b.status === "booking" ? 1 : 0) || normMerk(a.merk).localeCompare(normMerk(b.merk)) || a.name.localeCompare(b.name));
  const printTenors = [...new Set(filtered.flatMap((c) => sortedPackages(c).flatMap((p) => (p.installments || []).map((ins) => ins.tenor))))].sort((a, b) => a - b);
  const printGroups = {};
  filtered.forEach((c) => { const b = normMerk(c.merk); (printGroups[b] = printGroups[b] || []).push(c); });
  const printBrands = Object.keys(printGroups).sort((a, b) => a.localeCompare(b));

  return (
    <div className="print-area min-h-screen bg-[#050505] text-zinc-100">
      <header className="print-hide border-b border-[#1A1A22] bg-[#050505]/80 backdrop-blur-2xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
          <img src="/logo.jpg" alt="ForzaMobil.id" className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10" />
          <div>
            <p className="font-heading font-extrabold text-zinc-50 leading-none">ForzaMobil.id</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">Pricelist Mobil Bekas</p>
          </div>
          <button data-testid="download-pricelist-button" onClick={() => window.print()} className="print-hide ml-auto inline-flex items-center h-9 px-4 rounded-md bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] text-sm font-semibold transition-colors duration-200">
            <Download className="w-4 h-4 mr-1" /> Unduh PDF
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <h1 className="print-hide text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-50 mb-2">Unit Tersedia</h1>
        <p className="print-hide text-sm text-zinc-400 mb-4">Harga cash, kredit & paket leasing. Hubungi kami untuk negosiasi & inspeksi unit.</p>
        <div className="print-hide grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="guarantee-strip">
          {[
            { icon: ShieldCheck, label: "Jaminan Bebas Banjir & Kecelakaan" },
            { icon: FileCheck2, label: "Jaminan Legalitas Dokumen" },
            { icon: Wrench, label: "Garansi Mesin & Transmisi" },
            { icon: PhoneCall, label: "Layanan Darurat 24 Jam" },
          ].map((g, i) => (
            <div key={i} data-testid={`guarantee-${i}`} className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-[#0A0A0C] border border-[#1A1A22]">
              <g.icon className="w-4 h-4 text-blue-500 shrink-0" />
              <p className="text-xs font-medium text-zinc-300 leading-snug">{g.label}</p>
            </div>
          ))}
        </div>
        <div className="print-hide flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 pointer-events-none" />
            <input
              data-testid="public-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari merk, model, plat nomor..."
              className="w-full h-10 pl-9 pr-3 rounded-md bg-[#0A0A0C] border border-[#1A1A22] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
          <select
            data-testid="public-branch-filter"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#0A0A0C] border border-[#1A1A22] text-sm text-zinc-100 focus:outline-none focus:border-blue-500 transition-colors duration-200"
          >
            <option value="all">Semua Cabang</option>
            {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            data-testid="public-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#0A0A0C] border border-[#1A1A22] text-sm text-zinc-100 focus:outline-none focus:border-blue-500 transition-colors duration-200"
          >
            <option value="all">Semua Status</option>
            <option value="siap_jual">Siap Jual</option>
            <option value="booking">Booking</option>
          </select>
        </div>

        {cars && <p className="print-hide text-xs text-zinc-500 mb-6" data-testid="result-count">{filtered.length} unit ditemukan</p>}
        {error && <p className="text-rose-500 text-sm">{error}</p>}
        {!cars && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="h-80 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />)}
          </div>
        )}
        {cars && cars.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center border border-dashed border-[#1A1A22] rounded-md">
            <Car className="w-10 h-10 text-zinc-600 mb-3" />
            <p className="text-zinc-300 font-medium">Belum ada unit tersedia saat ini</p>
            <p className="text-sm text-zinc-500 mt-1">Silakan cek kembali nanti</p>
          </div>
        )}

        {cars && cars.length > 0 && filtered.length === 0 && (
          <p data-testid="public-empty-filter" className="text-sm text-zinc-500 py-10 text-center">Tidak ada unit yang cocok dengan pencarian/filter Anda.</p>
        )}

        <div className="print-hide grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c, i) => (
            <div key={c.id} data-testid={`public-car-${c.id}`} className="pcard fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-hidden hover:-translate-y-[2px] hover:border-[#26262E] transition-all duration-200" style={{ animationDelay: `${i * 60}ms`, transitionProperty: "transform, border-color" }}>
              <CardGallery car={c} />
              <div className="p-5">
                <p className="font-heading font-bold text-lg text-zinc-50 leading-snug">{c.name}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {new Intl.NumberFormat("id-ID").format(c.km || 0)} km · {c.branch_name}
                  {c.tax_status ? ` · Pajak ${c.tax_status === "hidup" ? "Hidup" : "Mati"}` : ""}
                  {c.tax_due_date ? ` s.d. ${fmtDate(c.tax_due_date)}` : ""}
                </p>
                <div className="mt-4 space-y-1.5">
                  {c.cash_price ? (
                    <div className="flex justify-between items-baseline">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cash</span>
                      <span className="font-mono tabular-nums text-lg font-semibold text-blue-500">{fmtIDR(c.cash_price)}</span>
                    </div>
                  ) : null}
                  {c.credit_price ? (
                    <div className="flex justify-between items-baseline">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Kredit</span>
                      <span className="font-mono tabular-nums text-zinc-100">{fmtIDR(c.credit_price)}</span>
                    </div>
                  ) : null}
                </div>
                {(c.credit_packages || []).length > 0 && (
                  <div className="mt-4 pt-3 border-t border-[#1A1A22] space-y-3">
                    {sortedPackages(c).map((p, pi) => (
                      <div key={`${p.leasing_name || "paket"}-${p.tdp}`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-blue-500 mb-1">{packageLabel(c, pi)}</p>
                        <p className="text-xs text-zinc-300 font-mono tabular-nums">TDP {fmtIDR(p.tdp || 0)}</p>
                        {(p.installments || []).map((ins) => (
                          <p key={`${ins.amount}x${ins.tenor}`} className="text-xs text-zinc-500 font-mono tabular-nums">
                            Angsuran {fmtIDR(ins.amount)} × {ins.tenor} bulan
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {(() => {
                  const wa = waLink(c.branch_phone, c);
                  const hasPhotos = (c.photo_count || 0) > 0;
                  if (!wa && !hasPhotos) return null;
                  return (
                    <div className="print-hide mt-4 flex gap-2">
                      {wa && (
                        <a
                          data-testid={`wa-${c.id}`}
                          href={wa}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center h-10 rounded-md bg-[#25D366] hover:bg-[#1EBE5D] text-white text-sm font-semibold shadow-[0_4px_12px_rgba(37,211,102,0.3)] transition-colors duration-200"
                        >
                          <MessageCircle className="w-4 h-4 mr-1.5" /> Chat WhatsApp{c.branch_name !== "-" ? ` — ${c.branch_name}` : ""}
                        </a>
                      )}
                      {hasPhotos && (
                        <DownloadPhotosButton car={c} compact={!!wa} />
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        <p className="print-hide text-center text-xs text-zinc-600 mt-12">ForzaMobil.id — Pricelist diperbarui secara real-time dari stok showroom</p>

        <div className="hidden print:block" data-testid="print-pricelist-table">
          <div className="flex items-center gap-3 mb-2 pb-2 border-b-2 border-zinc-800">
            <img src="/logo.jpg" alt="ForzaMobil.id" className="w-10 h-10 rounded-full object-cover" />
            <div className="flex-1">
              <h2 className="text-lg font-bold leading-none">FORZAMOBIL.ID</h2>
              <p className="text-[10px] uppercase tracking-[0.2em] mt-0.5">Pricelist Stok Ready</p>
            </div>
            <div className="text-right">
              <p className="text-base font-bold leading-none">PRICELIST</p>
              <p className="text-xs mt-0.5">{new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          </div>
          <p className="text-[10px] mb-1 italic">Note: baris berwarna merah = unit booking (sedang proses)</p>
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr>
                <th className="border border-zinc-400 px-1.5 py-1 text-center w-7">No</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-left">No. Polisi</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-left">Jenis Kendaraan</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-left">Cabang</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-right">KM</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-right">Harga Cash</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-right">Harga Kredit</th>
                <th className="border border-zinc-400 px-1.5 py-1 text-right">TDP</th>
                {printTenors.map((t) => (
                  <th key={t} className="border border-zinc-400 px-1.5 py-1 text-right">x{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                let rowNo = 0;
                return printBrands.map((brand) => (
                  <Fragment key={brand}>
                    <tr className="print-brand">
                      <td colSpan={8 + printTenors.length} className="border border-zinc-400 px-1.5 py-1 font-bold uppercase tracking-wider text-left">{brand}</td>
                    </tr>
                    {printGroups[brand].map((c) => {
                      rowNo += 1;
                      const pkgs = sortedPackages(c);
                      return (
                        <tr key={c.id} className={`print-zebra ${c.status === "booking" ? "print-booking" : ""}`}>
                          <td className="border border-zinc-400 px-1.5 py-1 text-center">{rowNo}</td>
                          <td className="border border-zinc-400 px-1.5 py-1 whitespace-nowrap font-medium">{c.plate || "-"}</td>
                          <td className="border border-zinc-400 px-1.5 py-1">{c.name}</td>
                          <td className="border border-zinc-400 px-1.5 py-1">{c.branch_name}</td>
                          <td className="border border-zinc-400 px-1.5 py-1 text-right">{fmtKm(c.km)}</td>
                          <td className="border border-zinc-400 px-1.5 py-1 text-right">{c.cash_price ? fmtNum(c.cash_price / 1000) : "-"}</td>
                          <td className="border border-zinc-400 px-1.5 py-1 text-right">{c.credit_price ? fmtNum(c.credit_price / 1000) : "-"}</td>
                          <td className="border border-zinc-400 px-1.5 py-1 text-right">
                            {pkgs.length === 0 ? "-" : pkgs.map((p, pi) => (
                              <p key={`${p.leasing_name || "paket"}-${p.tdp}`} className={`leading-4 whitespace-nowrap ${pi < pkgs.length - 1 ? "border-b border-zinc-300 pb-1 mb-1" : ""}`}>{fmtNum((p.tdp || 0) / 1000)}</p>
                            ))}
                          </td>
                          {printTenors.map((t) => (
                            <td key={t} className="border border-zinc-400 px-1.5 py-1 text-right">
                              {pkgs.length === 0 ? "-" : pkgs.map((p, pi) => {
                                const ins = (p.installments || []).find((x) => x.tenor === t);
                                return <p key={`${p.leasing_name || "paket"}-${p.tdp}-${t}`} className={`leading-4 whitespace-nowrap ${pi < pkgs.length - 1 ? "border-b border-zinc-300 pb-1 mb-1" : ""}`}>{ins ? fmtNum(ins.amount / 1000) : "-"}</p>;
                              })}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </Fragment>
                ));
              })()}
            </tbody>
          </table>
          <p className="text-[10px] mt-2">Semua harga & angsuran dalam ribuan Rupiah (contoh: 150.000 = Rp 150.000.000). TDP & angsuran dapat berbeda sesuai leasing dan profil kredit. Hubungi kami via WhatsApp untuk negosiasi & inspeksi unit.</p>
        </div>
      </main>
    </div>
  );
}
